import {
  Static,
  Object,
  String,
  Literal,
  Intersect,
  Union,
  Integer,
  Array,
  Optional,
  Partial,
  Number,
} from '@sinclair/typebox';

import { AwardBaggageAllowance, TripType } from '../../sas-domain/common';
import { Passenger } from '../../sas-domain/passengers';
import { Coupon } from '../../sas-domain/pointsPricing';

export const AwardCartRequestParams = Object({
  id: String(),
});

const JourneyBaseBody = Object({
  adults: Integer(),
  children: Integer(),
  infants: Integer(),
  youths: Integer(),
  offerId: String(),
  selectedCouponCodes: Array(String()),
  coupons: Array(Coupon),
  outboundSelectorId: String(),
});

const JourneyOneWayBody = Intersect([
  JourneyBaseBody,
  Object({
    tripType: Literal(TripType.ONE_WAY),
  }),
]);

const JourneyRoundTripBody = Intersect([
  JourneyBaseBody,
  Object({
    tripType: Literal(TripType.ROUND_TRIP),
    inboundSelectorId: String(),
  }),
]);

export const AwardJourneyRequestBody = Union([JourneyOneWayBody, JourneyRoundTripBody]);

export type AwardJourneyRequestBody = Static<typeof AwardJourneyRequestBody>;

export const TaxItem = Object({
  code: String(),
  value: Number(),
  label: Optional(String()),
});

export const AwardPriceItem = Object({
  type: String(),
  taxes: Array(TaxItem),
});

export const AwardTaxes = Object({
  totalTax: Number(),
  totalTaxBreakdown: Array(TaxItem),
  taxBreakdownByPassengerType: Array(
    Object({
      passengerType: String(),
      totalTax: Number(),
      taxBreakdown: Array(TaxItem),
    })
  ),
});

export const AwardPrices = Object({
  totalPrice: Number(),
  totalDiscountedPrice: Optional(Number()),
  pricePerPassenger: Array(
    Object({
      passengerId: String(),
      price: Number(),
      discountedPrice: Optional(Number()),
    })
  ),
});

export type AwardTaxes = Static<typeof AwardTaxes>;
export type AwardPrices = Static<typeof AwardPrices>;
export type AwardPriceItem = Static<typeof AwardPriceItem>;

export const AwardPassengerRequestBody = Array(Passenger);
export type AwardPassengerRequestBody = Static<typeof AwardPassengerRequestBody>;

export const SignedInUser = Object({
  firstName: String(),
  lastName: String(),
  initials: String(),
  tier: String(),
  points: Number(),
  euroBonusNumber: String(),
  sessionId: Optional(String()),
});

export type SignedInUser = Static<typeof SignedInUser>;

export const AwardCart = Intersect([
  Object({
    id: String(),
    createdAt: String(),
    signedInUser: SignedInUser,
    selectedCouponCodes: Array(String()),
    coupons: Array(Coupon),
  }),
  Partial(
    Object({
      taxes: AwardTaxes,
      prices: AwardPrices,
      offerId: String(),
      outboundSelectorId: String(),
      tripType: Union([Literal(TripType.ONE_WAY), Literal(TripType.ROUND_TRIP)]),
      inboundSelectorId: String(),
      baggageAllowance: Optional(AwardBaggageAllowance),
      passengers: Array(Passenger),
      orderId: String(),
    })
  ),
]);

export type AwardCart = Static<typeof AwardCart>;
